import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { CalendarDays, Clock, Target, Flame, CheckCircle2, Trophy } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayNarrator } from '../../../mascot';
import { getModuleIcon, MODULES, getModuleColors } from '../../../content';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { MS_PER_DAY } from '../../../config/constants';
import { getWeeklyActivityForLearner, getRecentSessionsForLearner } from '../../../utils/activityHelpers';
import { DataFreshness } from '../../shared/DataFreshness';
import { getChildLastActivity } from '../shared/childActivity';
import { useRefreshChild } from '../hooks/useRefreshChild';
interface ActivityPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  setViewingLearner: (id: string) => void;
}
export function ActivityPage({
  linkedLearners,
  selectedChild,
  childData,
  setViewingLearner
}: ActivityPageProps) {
  const {
    refresh
  } = useRefreshChild(selectedChild ? [selectedChild] : []);
  const {
    weeklyActivity,
    recentSessions,
    calendarWeeks,
    streakStats,
    weeklyStats
  } = useMemo(() => {
    if (!selectedChild || !childData) {
      return {
        weeklyActivity: [],
        recentSessions: [],
        calendarWeeks: [],
        streakStats: null,
        weeklyStats: {
          questions: 0,
          accuracy: 0,
          days: 0
        }
      };
    }
    const questionStates = childData.questionStates || {};
    // 1. Weekly Activity (current week)
    const weekly = getWeeklyActivityForLearner(questionStates);
    // 2. Recent Sessions
    const sessions = getRecentSessionsForLearner(questionStates, 7);
    // 3. Streak Stats — W4-5: slimmed from 4 cards to 2 (audit F-STAT-4).
    // Removed totalDailyGoalsReached + consecutiveFullWeeks: gamification
    // reporting with low parent action-value, mirrors the removed Prestaties-tab.
    const streakStats = {
      currentWeekDays: childData.dailyStreak?.currentWeekDays || 0,
      longestTotalStreak: childData.streak?.longestTotalStreak || 0
    };
    // 4. Weekly Summary Stats
    let questionsThisWeek = 0;
    let daysPracticedThisWeek = 0;
    weekly.forEach(day => {
      questionsThisWeek += day.questionCount;
      if (day.practiced) daysPracticedThisWeek++;
    });
    // Calculate accuracy for this week from recent sessions
    const now = Date.now();
    const oneWeekAgo = now - 7 * MS_PER_DAY;
    const sessionsThisWeek = sessions.filter(s => s.date >= oneWeekAgo);
    let totalQs = 0;
    let totalCorrect = 0;
    sessionsThisWeek.forEach(s => {
      totalQs += s.questionCount;
      totalCorrect += s.correctCount;
    });
    const accuracyThisWeek = totalQs > 0 ? Math.round(totalCorrect / totalQs * 100) : 0;
    // 5. 4-Week Calendar Grid
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const currentMonday = new Date(today);
    currentMonday.setDate(currentMonday.getDate() + mondayOffset);
    const weeks = [];
    for (let w = 3; w >= 0; w--) {
      const weekMonday = new Date(currentMonday);
      weekMonday.setDate(weekMonday.getDate() - w * 7);
      const weekMondayTs = weekMonday.getTime();
      const days = [];
      for (let d = 0; d < 7; d++) {
        const dayTs = weekMondayTs + d * MS_PER_DAY;
        const dayDate = new Date(dayTs);
        const isToday = dayTs === today.getTime();
        const isFuture = dayTs > today.getTime();
        // Count questions for this specific day
        let qCount = 0;
        for (const state of Object.values(questionStates)) {
          if (!state.history) continue;
          for (const attempt of state.history) {
            const attemptDate = new Date(attempt.timestamp);
            attemptDate.setHours(0, 0, 0, 0);
            if (attemptDate.getTime() === dayTs) {
              qCount++;
            }
          }
        }
        days.push({
          date: dayDate,
          isToday,
          isFuture,
          questionCount: qCount,
          practiced: qCount > 0
        });
      }
      // Label: e.g., "12 - 18 mrt"
      const startStr = `${weekMonday.getDate()} ${weekMonday.toLocaleString('nl-NL', {
        month: 'short'
      })}`;
      const endDay = new Date(weekMondayTs + 6 * MS_PER_DAY);
      const endStr = `${endDay.getDate()} ${endDay.toLocaleString('nl-NL', {
        month: 'short'
      })}`;
      weeks.push({
        label: w === 0 ? 'Deze week' : w === 1 ? 'Vorige week' : `${startStr} - ${endStr}`,
        days
      });
    }
    return {
      weeklyActivity: weekly,
      recentSessions: sessions,
      calendarWeeks: weeks,
      streakStats,
      weeklyStats: {
        questions: questionsThisWeek,
        accuracy: accuracyThisWeek,
        days: daysPracticedThisWeek
      }
    };
  }, [selectedChild, childData]);
  if (!selectedChild || !childData) {
    return <PageLayout title="Activiteit" description="Bekijk de oefenhistorie" data-id="element-8470" data-mp="227p1lp9ul9pl">
        <div className="flex flex-col items-center justify-center py-20 text-center space-y-4" data-id="element-8471" data-mp="1y3zk1jrlbse2">
          <DjayNarrator size="lg" emotion="thinking" data-id="element-8472" data-mp="2c38x4aki26dt" />
          <p className="text-slate-500 font-medium" data-id="element-8473" data-mp="275qm8emx9h36">
            Selecteer een kind om de activiteit te bekijken.
          </p>
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Activiteit" description={`Bekijk wanneer en hoe ${selectedChild.displayName} oefent`} actions={<DataFreshness updatedAt={getChildLastActivity(childData)} onRefresh={refresh} data-id="element-8475" data-mp="2gxmlvu4npf9i" />} data-id="element-8474" data-mp="2ltk5qaf4p3r1">
      
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-8476" data-mp="2usozz4jnqaxe">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8477" data-mp="2px9ogj94gr45" />
        

        {/* Weekly Summary Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-8478" data-mp="1sh3v5zy22uuj">
          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-8479" data-mp="1sfixw2kcwxsz">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-8480" data-mp="1wh48tidbz20j">
              <CalendarDays className="w-5 h-5 text-emerald-500" data-id="element-8481" data-mp="21bs35xv0mqn1" />
            </div>
            <div data-id="element-8482" data-mp="26b4mw9qlbhle">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-8483" data-mp="2b84tclrcqmd1">
                Dagen geoefend
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-8484" data-mp="2g897w68p970m">
                {weeklyStats.days}{' '}
                <span className="text-sm font-bold text-slate-400" data-id="element-8485" data-mp="2k7i6boeopt2i">
                  / 7 deze week
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-8486" data-mp="pu0e3o3c4815">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-8487" data-mp="ibsocnpwu45f">
              <Target className="w-5 h-5 text-blue-500" data-id="element-8488" data-mp="dkasuf6novru" />
            </div>
            <div data-id="element-8489" data-mp="8l6gxgsckjxt">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-8490" data-mp="8koeu3j07bhz">
                Vragen beantwoord
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-8491" data-mp="4hrqdw6s8l64">
                {weeklyStats.questions}{' '}
                <span className="text-sm font-bold text-slate-400" data-id="element-8492" data-mp="117r8ah8tow1d">
                  deze week
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-8493" data-mp="s7m0i5zpvvmo">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-8494" data-mp="n9u8lbz2o9ih">
              <CheckCircle2 className="w-5 h-5 text-purple-500" data-id="element-8495" data-mp="3d0z65aehdpqe" />
            </div>
            <div data-id="element-8496" data-mp="37z054767r4wm">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-8497" data-mp="382nyn6bnrrbc">
                Nauwkeurigheid
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-8498" data-mp="1fzjpjmerq6dn">
                {weeklyStats.accuracy}%{' '}
                <span className="text-sm font-bold text-slate-400" data-id="element-8499" data-mp="120aj3egd6qmv">
                  deze week
                </span>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-8500" data-mp="1unein3woucr9">
          {/* Left Column: Calendar & Streaks */}
          <div className="lg:col-span-2 space-y-8" data-id="element-8501" data-mp="1zlzg4vkk2ic0">
            {/* 4-Week Calendar */}
            <motion.section initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-8502" data-mp="1kywkyvjpglru">
              
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-8503" data-mp="2dl7jm0nr4cm0">
                <CalendarDays className="w-4 h-4 text-emerald-500" data-id="element-8504" data-mp="2ifljaf9g55wh" />
                Oefenkalender
              </h2>
              <Card className="p-6 bg-white border-slate-200 shadow-sm" data-id="element-8505" data-mp="6w709ecxoz4e">
                <div className="flex justify-between items-end mb-6" data-id="element-8506" data-mp="1z6r2uksfg92">
                  <div className="flex gap-4 text-xs font-medium text-slate-500" data-id="element-8507" data-mp="fpsd2s5jg6b6">
                    <div className="flex items-center gap-1.5" data-id="element-8508" data-mp="boqdubbmfu97">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-8509" data-mp="brm9tu3ws8v0" />{' '}
                      Geoefend
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-8510" data-mp="3f6qo5x4ngbpz">
                      <div className="w-3 h-3 rounded-full bg-slate-100" data-id="element-8511" data-mp="3t5qb90cikik1" /> Niet
                      geoefend
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-8512" data-mp="3o8rhwk0ocfmw">
                  {/* Day headers — F-ACT-2: responsive label column (44px mobile → 80px sm+) */}
                  <div className="grid grid-cols-[44px_repeat(7,1fr)] sm:grid-cols-[80px_repeat(7,1fr)] gap-1 sm:gap-2 mb-2" data-id="element-8513" data-mp="13gxrr3zqssjw">
                    <div data-id="element-8514" data-mp="um3ppxgh8r7w"></div>
                    {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => <div key={day} className="text-center text-xs font-bold text-slate-400" data-id="element-8515" data-mp="ppd9na4o9vmh">
                      
                        {day}
                      </div>)}
                  </div>

                  {/* Weeks */}
                  {calendarWeeks.map((week, i) => <div key={i} className="grid grid-cols-[44px_repeat(7,1fr)] sm:grid-cols-[80px_repeat(7,1fr)] gap-1 sm:gap-2 items-center" data-id="element-8516" data-mp="bwxeswlxd3wn" data-mpi={i}>
                    
                      <div className="text-[10px] sm:text-xs font-medium text-slate-500 text-right pr-1 sm:pr-4 truncate" data-id="element-8517" data-mp="6wj2v3h7d9tc">
                        {week.label}
                      </div>
                      {week.days.map((day, j) => {
                    const dateLabel = day.date.toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short'
                    });
                    const ariaLabel = day.isFuture ? `${dateLabel}: in de toekomst` : day.practiced ? `${dateLabel}: ${day.questionCount} vragen geoefend${day.isToday ? ' (vandaag)' : ''}` : `${dateLabel}: niet geoefend${day.isToday ? ' (vandaag)' : ''}`;
                    return <div key={j} className="flex justify-center" data-id="element-8518" data-mp="3jzwnk5jtih8f" data-mpi={j}>
                            <div role="img" aria-label={ariaLabel} className={`rounded-full transition-all ${day.isFuture ? 'w-6 h-6 bg-transparent border border-slate-100' : day.practiced ? day.isToday ? 'w-8 h-8 bg-emerald-500 shadow-md shadow-emerald-200 ring-2 ring-emerald-300 ring-offset-1' : 'w-6 h-6 bg-emerald-400' : day.isToday ? 'w-8 h-8 bg-slate-100 border-2 border-emerald-200 ring-2 ring-emerald-200 ring-offset-1' : 'w-6 h-6 bg-slate-100'} flex items-center justify-center`} title={day.isFuture ? '' : ariaLabel} data-id="element-8519" data-mp="3o0q9wuhm3nz7">
                            
                              {day.isToday && day.practiced && <CheckCircle2 className="w-4 h-4 text-white" data-id="element-8520" data-mp="kj85kx11sz3j" />}
                            </div>
                          </div>;
                  })}
                    </div>)}
                </div>
              </Card>
            </motion.section>

            {/* Streak Stats */}
            <motion.section initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} data-id="element-8521" data-mp="2qksk84k2e4hs">
              
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-8522" data-mp="1xvddhqzfmxro">
                <Flame className="w-4 h-4 text-orange-500" data-id="element-8523" data-mp="1syd3x63djcvt" />
                Reeksen
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-8524" data-mp="27mq126e771rw">
                <Card className="p-6 bg-white border-slate-200 shadow-sm flex flex-col justify-center items-center text-center" data-id="element-8525" data-mp="22vhsfckozkfs">
                  <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-3" data-id="element-8526" data-mp="1f4lwq4d48539">
                    <Flame className="w-7 h-7 text-orange-500" data-id="element-8527" data-mp="1a5reb3j28ijo" />
                  </div>
                  <div className="text-4xl font-black text-slate-800 mb-1" data-id="element-8528" data-mp="3lsuu87k50z7s">
                    {streakStats?.currentWeekDays || 0}
                  </div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-8529" data-mp="3ct7urrayy3hd">
                    Dagen op rij
                  </div>
                </Card>

                <Card className="p-6 bg-white border-slate-200 shadow-sm flex flex-col justify-center items-center text-center" data-id="element-8530" data-mp="3gv2wgwwtaivo">
                  <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3" data-id="element-8531" data-mp="de484amc88be">
                    <Trophy className="w-7 h-7 text-amber-500" data-id="element-8532" data-mp="3vjz7n2u60c0i" />
                  </div>
                  <div className="text-4xl font-black text-slate-800 mb-1" data-id="element-8533" data-mp="4deftgwad02f">
                    {streakStats?.longestTotalStreak || 0}
                  </div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-8534" data-mp="2k2zb1v0sbb0o">
                    Langste reeks ooit
                  </div>
                </Card>
              </div>
            </motion.section>
          </div>

          {/* Right Column: Recent Sessions */}
          <div className="space-y-8" data-id="element-8535" data-mp="2zls7472tlo94">
            <motion.section initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} data-id="element-8536" data-mp="2zkqva0dzcxqc">
              
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-8537" data-mp="3nco3lhi817mp">
                <Clock className="w-4 h-4 text-blue-500" data-id="element-8538" data-mp="3ie4hnh3mnvuc" />
                Recente Sessies
              </h2>

              <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-8539" data-mp="3ea514yickx0z">
                {recentSessions.length > 0 ? <div className="divide-y divide-slate-100" data-id="element-8540" data-mp="39c3l6mg7xdtq">
                    {recentSessions.map((session, i) => {
                  const IconComp = getModuleIcon(session.moduleIcon);
                  const moduleData = session.moduleId ? MODULES.find(m => m.id === session.moduleId) : null;
                  const colors = getModuleColors(moduleData?.color || 'text-slate-500');
                  const date = new Date(session.date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  const dateStr = isToday ? 'Vandaag' : date.toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'short'
                  });
                  const timeStr = date.toLocaleTimeString('nl-NL', {
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  return <div key={i} className="p-4 hover:bg-slate-50 transition-colors" data-id="element-8541" data-mp="1655vs80t8scv" data-mpi={i}>
                        
                          <div className="flex items-center justify-between mb-3" data-id="element-8542" data-mp="1a4n4dabja5m9">
                            <div className="text-xs font-bold text-slate-400" data-id="element-8543" data-mp="37wshbu1xnv1y">
                              {dateStr} om {timeStr}
                            </div>
                            <div className={`text-xs font-bold px-2 py-0.5 rounded-full ${session.accuracy >= 80 ? 'bg-emerald-100 text-emerald-700' : session.accuracy >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-8544" data-mp="j1szrs8fcre4">
                            
                              {session.accuracy}% goed
                            </div>
                          </div>

                          <div className="flex items-center gap-3" data-id="element-8545" data-mp="120x3clh75sye">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.light} ${colors.text}`} data-id="element-8546" data-mp="11z40zidm9uw7">
                            
                              <IconComp className="w-5 h-5" data-id="element-8547" data-mp="1bqgsg8vi6j1p" />
                            </div>
                            <div className="min-w-0" data-id="element-8548" data-mp="16sf8llq96fp6">
                              <div className="font-bold text-sm text-slate-900 truncate" data-id="element-8549" data-mp="1kretcianaag2">
                                {session.moduleTitle}
                              </div>
                              <div className="text-xs font-medium text-slate-500" data-id="element-8550" data-mp="1fqqxnjbh15fi">
                                {session.questionCount} vragen geoefend
                              </div>
                            </div>
                          </div>
                        </div>;
                })}
                  </div> : <div className="p-8 text-center" data-id="element-8551" data-mp="1ufai2zgzvapz">
                    <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-8552" data-mp="1udpj8xnnd5mb">
                      <Clock className="w-6 h-6 text-slate-300" data-id="element-8553" data-mp="1glsv14p3nfv7" />
                    </div>
                    <p className="text-sm font-medium text-slate-500" data-id="element-8554" data-mp="1lfx5hxxkq611">
                      Nog geen recente sessies.
                    </p>
                  </div>}
              </Card>
            </motion.section>
          </div>
        </div>
      </div>
    </PageLayout>;
}